import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStore } from '@nanostores/react';
import { $isAuthenticated, $authLoading } from '~/lib/stores/auth';
import { pendingClaimIntent } from '~/lib/stores/claimIntent';

/*
 * Resume ONCE per page load. Without this the hook is a trap: it re-fires on
 * every route change, so a signed-in visitor who decides not to claim and
 * clicks away is dragged straight back to /claim/<token> with no escape but
 * claiming. One bounce is the resume; anything after that is the visitor's own
 * navigation and must be left alone. A fresh load resets it — which is exactly
 * the magic-link redirect-to-root case the resume exists for.
 */
let resumed = false;

/**
 * Post-auth claim resume (DEV-170). The client has no general post-login resume
 * (auth is a modal; the magic-link flow hard-redirects to `/`), so a claim
 * started before sign-in would be dropped. The claim page stashes its token in
 * localStorage; this hook — mounted once in AppRoutes, like useCheckoutReturn —
 * watches auth and sends a signed-in user back to /claim/<token>.
 *
 * The intent is NOT cleared here: the claim page owns clearing it (on success,
 * or on a terminal state for that same token), so a mid-flow reload still
 * resumes and the page can auto-claim on arrival.
 */
export function useClaimIntentResume(): void {
  const isAuthenticated = useStore($isAuthenticated);
  const authLoading = useStore($authLoading);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading || !isAuthenticated) {
      return;
    }

    const token = pendingClaimIntent();

    if (token && !resumed && !location.pathname.startsWith('/claim/')) {
      resumed = true;
      navigate(`/claim/${token}`, { replace: true });
    }
  }, [authLoading, isAuthenticated, location.pathname, navigate]);
}
